import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { type FrontInboxInfoT, type FrontInfo, IMPORT_STATE } from '../types';

export const useFront = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspace_alias = currWorkspace?.alias;
  if (!workspace_alias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(SOURCE_TYPE.FRONT, workspace_alias);

  const { data } = useQuery({
    queryKey: queryKeys,
    queryFn: async () => service.front.getFrontInfo(workspace_alias),
    enabled: enabled,
  });

  const { mutate: disconnectFrontMutation } = useMutation({
    mutationFn: () => {
      const frontWorkspace = queryClient.getQueryData(queryKeys) as FrontInfo;
      return service.front.deleteFrontInfo(frontWorkspace.alias);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: (dateRange: NullableDateRangeT) => {
      if (!dateRange.start_date || !dateRange.end_date) {
        throw new Error('Date range is required');
      }
      return service.front.importWorkspaceFeedback(
        workspace_alias,
        dateRange.start_date,
        dateRange.end_date,
      );
    },
    onSuccess: () => {
      const frontWorkspace = queryClient.getQueryData(queryKeys) as FrontInfo;
      queryClient.setQueryData(queryKeys, {
        ...frontWorkspace,
        import_state: IMPORT_STATE.IMPORTING,
      });
    },
  });

  const { mutate: updateInboxesMutation } = useMutation({
    mutationFn: async ({
      info,
      action,
    }: {
      info: FrontInboxInfoT;
      action: 'push' | 'pull';
    }) => {
      const frontWorkspace = queryClient.getQueryData(queryKeys) as FrontInfo;
      if (action === 'push') {
        return service.front.pushInboxInfo(frontWorkspace.alias, info);
      } else {
        return service.front.pullInboxInfo(frontWorkspace.alias, info);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: updatePrivacyMutation } = useMutation({
    mutationFn: (privacy: boolean) => service.front.updateFrontPrivacy(privacy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: fetchInboxInfosMutation } = useMutation({
    mutationFn: () => {
      const frontWorkspace = queryClient.getQueryData(queryKeys) as FrontInfo;
      return service.front.getInboxInfos(frontWorkspace.alias);
    },
  });

  return {
    frontWorkspace: data,
    disconnectFrontMutation,
    requestImportMutation,
    updateInboxesMutation,
    updatePrivacyMutation,
    fetchInboxInfosMutation,
  };
};
